import { useState } from "react";
import { Send, ArrowRight, MessageCircle, Check } from "lucide-react";
import { CITIES } from "../cityConfig";

/**
 * Bouquet lead form — sits under city catalog, in footer, and on home page.
 * Posts to /api/leads/bouquet (same-origin) which sends Telegram notification.
 *
 * Props:
 *   city — city slug (e.g. "antalya"). Optional on home page.
 *   lang — "ru" | "en" | local code. Defaults to "ru".
 *   variant — "section" (default, big block) | "compact" (footer card).
 */
export default function BouquetLeadForm({ city = "", lang = "ru", variant = "section" }) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    delivery_date: "",
    occasion: "",
    budget: "",
    message: "",
  });
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  const T = {
    ru: {
      eyebrow: "ИНДИВИДУАЛЬНЫЙ БУКЕТ",
      title: "Не нашли что искали?",
      titleAccent: "Соберём под вас",
      subtitle: "Опишите букет вашей мечты — флорист свяжется в течение 30 минут и предложит композицию под бюджет, повод и стиль.",
      name: "Ваше имя",
      contact: "WhatsApp / Telegram / Email",
      date: "Когда нужен букет",
      occasion: "Повод",
      occasionOpts: ["Романтика / Свидание", "День рождения", "Годовщина", "Извинение", "Просто так", "Корпоратив / Партнёр", "Похороны", "Другое"],
      budget: "Бюджет",
      budgetOpts: ["до €60", "€60–€120", "€120–€200", "€200–€400", "€400+"],
      message: "Что важно учесть? (любимые цветы, цвета, стиль)",
      submit: "ОТПРАВИТЬ ЗАЯВКУ",
      sending: "Отправляем…",
      required: "Заполните имя и контакт (минимум 4 символа)",
      successTitle: "Спасибо! Заявка отправлена",
      successText: "Флорист свяжется с вами в ближайшие 30 минут через указанный контакт.",
      errorText: "Не удалось отправить. Напишите нам в WhatsApp — заберём заказ оттуда.",
    },
    en: {
      eyebrow: "CUSTOM BOUQUET",
      title: "Didn't find what you wanted?",
      titleAccent: "We'll craft it for you",
      subtitle: "Describe your dream bouquet — our florist will reply within 30 minutes with an arrangement matched to your budget, occasion and style.",
      name: "Your name",
      contact: "WhatsApp / Telegram / Email",
      date: "When do you need it",
      occasion: "Occasion",
      occasionOpts: ["Romantic / Date", "Birthday", "Anniversary", "Apology", "Just because", "Corporate / Partner", "Funeral", "Other"],
      budget: "Budget",
      budgetOpts: ["under €60", "€60–€120", "€120–€200", "€200–€400", "€400+"],
      message: "What matters most? (favorite flowers, colors, style)",
      submit: "SEND REQUEST",
      sending: "Sending…",
      required: "Please fill in name and contact (min 4 chars)",
      successTitle: "Thank you! Request received",
      successText: "Our florist will contact you within 30 minutes via the contact you provided.",
      errorText: "Could not send. Message us on WhatsApp — we'll take it from there.",
    },
    tr: {
      eyebrow: "ÖZEL BUKET",
      title: "Aradığınızı bulamadınız mı?",
      titleAccent: "Size özel hazırlarız",
      subtitle: "Hayalinizdeki buketi anlatın — çiçekçimiz 30 dakika içinde bütçe, vesile ve stilinize uygun bir kompozisyonla geri dönecek.",
      name: "Adınız",
      contact: "WhatsApp / Telegram / E-posta",
      date: "Ne zaman lazım",
      occasion: "Vesile",
      occasionOpts: ["Romantik / Randevu", "Doğum günü", "Yıldönümü", "Özür", "Sebepsiz", "Kurumsal / İş Ortağı", "Cenaze", "Diğer"],
      budget: "Bütçe",
      budgetOpts: ["€60 altı", "€60–€120", "€120–€200", "€200–€400", "€400+"],
      message: "Önemli bir not? (sevdiği çiçekler, renkler, stil)",
      submit: "GÖNDER",
      sending: "Gönderiliyor…",
      required: "Lütfen ad ve iletişim bilgisi girin (min 4 karakter)",
      successTitle: "Teşekkürler! Talebiniz alındı",
      successText: "Çiçekçimiz 30 dakika içinde belirttiğiniz iletişim üzerinden sizinle iletişime geçecek.",
      errorText: "Gönderilemedi. WhatsApp'tan yazın — oradan devam ederiz.",
    },
    es: {
      eyebrow: "RAMO PERSONALIZADO",
      title: "¿No encontraste lo que buscabas?",
      titleAccent: "Lo creamos para ti",
      subtitle: "Describe el ramo de tus sueños — nuestro florista responderá en 30 minutos con una composición ajustada a tu presupuesto, ocasión y estilo.",
      name: "Tu nombre",
      contact: "WhatsApp / Telegram / Email",
      date: "Cuándo lo necesitas",
      occasion: "Ocasión",
      occasionOpts: ["Romántico / Cita", "Cumpleaños", "Aniversario", "Disculpa", "Sin motivo", "Corporativo / Socio", "Funeral", "Otro"],
      budget: "Presupuesto",
      budgetOpts: ["menos de €60", "€60–€120", "€120–€200", "€200–€400", "€400+"],
      message: "¿Algo importante? (flores favoritas, colores, estilo)",
      submit: "ENVIAR",
      sending: "Enviando…",
      required: "Completa nombre y contacto (mín. 4 caracteres)",
      successTitle: "¡Gracias! Solicitud recibida",
      successText: "Nuestro florista te contactará en los próximos 30 minutos.",
      errorText: "No se pudo enviar. Escríbenos por WhatsApp.",
    },
  };
  const t = T[lang] || T.ru;
  const curSym = (city && CITIES[city] && CITIES[city].currencySymbol) || "€";
  const budgetOpts = t.budgetOpts.map((o) => o.replace(/€/g, curSym));

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || form.name.trim().length < 2 || !form.contact.trim() || form.contact.trim().length < 4) {
      setErr(t.required);
      return;
    }
    setState("sending");
    try {
      const r = await fetch(`/api/leads/bouquet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          city,
          lang,
          source_page: typeof window !== "undefined" ? window.location.pathname : "",
        }),
      });
      if (!r.ok) throw new Error("bad");
      setState("success");
    } catch (_e) {
      setState("error");
    }
  };

  const isCompact = variant === "compact";

  if (state === "success") {
    return (
      <div className={isCompact
        ? "bg-white/[0.04] border border-[#C9A87C]/30 rounded-sm p-6 text-center"
        : "bg-gradient-to-br from-pink-50 via-white to-rose-50 border border-pink-100 rounded-3xl p-8 sm:p-12 text-center shadow-sm"}
        data-testid="bouquet-lead-success">
        <div className={isCompact
          ? "w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#C9A87C] text-white flex items-center justify-center mx-auto mb-4"
          : "w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center mx-auto mb-4 shadow-lg shadow-pink-200"}>
          <Check className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
        </div>
        <h3 className={isCompact
          ? "text-lg text-white mb-2 font-medium"
          : "text-xl sm:text-2xl text-gray-900 mb-3 font-bold"}
          style={!isCompact ? { fontFamily: "'Playfair Display', serif" } : undefined}>{t.successTitle}</h3>
        <p className={isCompact ? "text-sm text-white/70 font-light" : "text-sm sm:text-base text-gray-500 leading-relaxed max-w-md mx-auto"}>{t.successText}</p>
      </div>
    );
  }

  // Compact version (footer)
  if (isCompact) {
    return (
      <form onSubmit={submit} className="space-y-3" data-testid="bouquet-lead-form-compact">
        <p className="text-[10px] tracking-[0.3em] text-[#C9A87C] mb-2 font-light">{t.eyebrow}</p>
        <h3 className="text-xl sm:text-2xl text-white mb-1 font-medium">{t.title}</h3>
        <p className="text-xs sm:text-sm text-white/60 mb-4 font-light leading-relaxed">{t.subtitle}</p>
        <input type="text" required placeholder={t.name} value={form.name} onChange={update("name")}
          className="w-full px-3 py-2.5 bg-white/[0.06] border border-white/15 focus:border-[#C9A87C] outline-none text-white text-sm placeholder-white/45 rounded-sm transition"
          data-testid="bouquet-lead-name" />
        <input type="text" required placeholder={t.contact} value={form.contact} onChange={update("contact")}
          className="w-full px-3 py-2.5 bg-white/[0.06] border border-white/15 focus:border-[#C9A87C] outline-none text-white text-sm placeholder-white/45 rounded-sm transition"
          data-testid="bouquet-lead-contact" />
        <textarea rows={2} placeholder={t.message} value={form.message} onChange={update("message")}
          className="w-full px-3 py-2.5 bg-white/[0.06] border border-white/15 focus:border-[#C9A87C] outline-none text-white text-sm placeholder-white/45 rounded-sm resize-none transition"
          data-testid="bouquet-lead-message" />
        {err && <p className="text-[#C9A87C] text-xs font-light" data-testid="bouquet-lead-error">{err}</p>}
        <button type="submit" disabled={state === "sending"}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#C9A87C] hover:bg-[#D4B896] text-[#1F1815] text-[11px] tracking-[0.25em] font-semibold rounded-sm transition disabled:opacity-60 disabled:cursor-wait"
          data-testid="bouquet-lead-submit">
          <Send className="w-3.5 h-3.5" /> {state === "sending" ? t.sending : t.submit}
        </button>
        {state === "error" && <p className="text-xs text-white/55 text-center pt-1">{t.errorText}</p>}
      </form>
    );
  }

  // Full section version (under catalog & on home) — shop style: pink/rose accent, Playfair, rounded-3xl
  const inputCls = "w-full px-4 py-3.5 bg-white border-2 border-gray-100 focus:border-pink-300 outline-none text-gray-900 text-sm placeholder-gray-400 rounded-2xl transition";
  return (
    <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 py-14 sm:py-20 lg:py-24" data-testid="bouquet-lead-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <span className="inline-block bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            {t.eyebrow}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t.title}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{t.titleAccent}</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-xl mx-auto leading-relaxed">{t.subtitle}</p>
        </div>

        <form onSubmit={submit} className="bg-white border-2 border-gray-100 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl shadow-pink-100/40 space-y-4 sm:space-y-5" data-testid="bouquet-lead-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <input type="text" required placeholder={t.name} value={form.name} onChange={update("name")}
              className={inputCls} data-testid="bouquet-lead-name" />
            <input type="text" required placeholder={t.contact} value={form.contact} onChange={update("contact")}
              className={inputCls} data-testid="bouquet-lead-contact" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            <input type="text" placeholder={t.date} value={form.delivery_date} onChange={update("delivery_date")}
              className={inputCls} data-testid="bouquet-lead-date" />
            <select value={form.occasion} onChange={update("occasion")}
              aria-label={t.occasion}
              className={`${inputCls} appearance-none cursor-pointer`}
              data-testid="bouquet-lead-occasion">
              <option value="">{t.occasion}</option>
              {t.occasionOpts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select value={form.budget} onChange={update("budget")}
              aria-label={t.budget}
              className={`${inputCls} appearance-none cursor-pointer`}
              data-testid="bouquet-lead-budget">
              <option value="">{t.budget}</option>
              {budgetOpts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <textarea rows={3} placeholder={t.message} value={form.message} onChange={update("message")}
            className={`${inputCls} resize-none`}
            data-testid="bouquet-lead-message" />
          {err && <p className="text-rose-500 text-sm text-center" data-testid="bouquet-lead-error">{err}</p>}
          <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4 pt-1">
            <button type="submit" disabled={state === "sending"}
              className="group inline-flex items-center justify-center gap-2 sm:gap-3 w-full sm:flex-1 px-6 sm:px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-sm font-semibold rounded-full shadow-lg shadow-pink-200 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait disabled:hover:scale-100"
              data-testid="bouquet-lead-submit">
              <Send className="w-4 h-4" />
              <span>{state === "sending" ? t.sending : t.submit}</span>
              {state !== "sending" && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
            </button>
            <a href="https://wa.me/905316224378" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 sm:w-auto px-6 sm:px-7 py-4 bg-[#25d366] hover:bg-[#20bd5a] text-white text-sm font-semibold rounded-full shadow-lg shadow-green-200 transition-all hover:scale-[1.02]">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
          {state === "error" && <p className="text-xs sm:text-sm text-gray-500 text-center pt-1" data-testid="bouquet-lead-error-fallback">{t.errorText}</p>}
        </form>
      </div>
    </section>
  );
}
